import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { Routes, Routes as RoutesInternal, useJourneyNavigation } from '../../../routes';
import { TableColumnStyle, TableRow } from '../../../widgets/table/TableWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { Route, Routes as RoutesSwitch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { useSystem } from '../../../hooks/useSystem';
import { SearchableTableWidget } from '../../../widgets/table/SearchableTableWidget';
import { LayerWidget } from '../../../layers/LayerWidget';
import { GitProvider } from '../create-app/CreateAppPage';
import { DeleteAppPage } from '../delete-app/DeleteAppPage';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { AppRowWidget } from './AppRowWidget';
import { useAllowed } from '../../../hooks/useAllowed';
import { ChangeSourceControlForm } from '../edit-app/ChangeSourceControlForm';
import { MigrateAppPage } from '../migrate-app/MigrateAppPage';
import { ChangeSourceModeForm } from '../edit-app/ChangeSourceModeForm';
import { getThemeForProduct, styled } from '../../../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setupTooltipProps } from '../../../utils/Tooltips';
import { getVersionControlMode } from '../../../utils/wording';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { APP_FEATURES, getAppUrl, getProductBasedOnFeatures, Product } from '@journeyapps-platform/types-accounts';
import { ENV } from '../../../env';
import { SmartCreateAppPage } from '../create-app/SmartCreateAppPage';
import { Region } from '../../../utils/environments';
import { AppModelAction } from '@journeyapps-platform/cardinal-catalog';
import { DateTime } from 'luxon';

const powersync = require('../../../../media/powersync-logo.png');

export interface AppRow extends TableRow {
  app: AppModel;
  legacy: boolean;
}

export const AppsPage: React.FC = observer(() => {
  const orgStore = useOrgStore();
  const { navigate, params, relativeRoute } = useJourneyNavigation(Routes.MANAGE_APPS);
  const system = useSystem();

  const [tempApp] = useState<AppModel>(
    system.appsStore.generate({
      id: '',
      auth_scope_id: '',
      name: '',
      region: Region.US,
      org_id: params.org_id,
      features: []
    })
  );
  const isAllowedCreateApp = useAllowed({ action: AppModelAction.CREATE, resource: tempApp });

  useEffect(() => {
    (async () => {
      const org = await system.orgStore.getOrg(params.org_id);
      await org.loadApps();
    })();
  }, [params.org_id]);

  const apps = orgStore.org(params.org_id!)?.apps;

  const getRows = (): AppRow[] => {
    if (!apps?.items) {
      return [];
    }
    return (
      _.chain(apps.items)
        // TODO: This will be controlled by the table
        .orderBy((a) => a.model.name.toLowerCase(), ['asc'])
        .map((app) => {
          return {
            key: app.id,
            app: app,
            legacy: !app.model.source,
            cells: {
              name: app.model.name,
              migrate: !app.model.source,
              git_mode: getVersionControlMode(app.model.vcs_mode!),
              version_control: GitProvider[app.model.source?.type!]
            }
          };
        })
        .value()
    );
  };

  const legacyApps = getRows().filter((a) => a.legacy);
  const normalApps = getRows().filter((a) => !a.legacy);

  return (
    <OrgPageWidget activeItemKey={Routes.MANAGE_APPS}>
      <PageHeadingWidget title={'Projects'} />
      <SearchableTableWidget<AppRow>
        loading={(!orgStore.org(params.org_id) || orgStore.org(params.org_id)?.apps.loading) && normalApps.length === 0}
        controlButtons={[
          {
            label: 'Create Project',
            disabled: !isAllowedCreateApp,
            action: () => {
              navigate(Routes.CREATE_APP, params);
            }
          }
        ]}
        onRowSelected={(event, row) => {
          if (!!row.app.model.locked) {
            system.dialogStore.infoDialog({
              title: 'Project locked',
              message: `To continue working on this project, you will need to upgrade to one of our paid plans.`
            });
            return;
          }
          window.open(
            getAppUrl({
              app: row.app.model,
              env: ENV
            }),
            '_blank'
          );
        }}
        columns={[
          {
            key: 'icon ',
            display: '',
            shrink: true,
            accessor: ({ row }) => {
              const product = getProductBasedOnFeatures(row.app.model.features);
              if (product === Product.POWERSYNC) {
                return (
                  <S.IconContainer product={product}>
                    <S.Icon background={powersync} />
                  </S.IconContainer>
                );
              }
              return (
                <S.IconContainer product={product}>
                  <S.Rocket icon="rocket" />
                </S.IconContainer>
              );
            }
          },
          {
            key: 'name',
            display: 'Project name',
            style: TableColumnStyle.PRIMARY,
            accessorSearch: (name) => {
              return name;
            },
            accessor: ({ cell, active, row }) => {
              if (row.app.model.locked) {
                return (
                  <S.AppName locked={true}>
                    <span>{cell} (Locked)</span>
                  </S.AppName>
                );
              }
              return (
                <S.AppName locked={false}>
                  <span>{cell}</span>
                  <span
                    {...setupTooltipProps({
                      tooltip: 'Edit project/app'
                    })}
                  >
                    <S.AppOpenIcon hover={!!active} icon="external-link" />
                  </span>
                </S.AppName>
              );
            }
          },
          {
            key: 'type',
            display: 'Type',
            accessor: ({ row }) => {
              if (row.app.model.trial) {
                if (row.app.model.locked) {
                  return (
                    <>
                      Trial <S.TrialExpire>(Expired)</S.TrialExpire>
                    </>
                  );
                }
                if (row.app.model.trial_expiry) {
                  let r = DateTime.fromISO(row.app.model.trial_expiry).toRelative({});
                  return (
                    <>
                      Trial <S.TrialExpire>(expires {r})</S.TrialExpire>
                    </>
                  );
                }
                return 'Trial';
              }
              return `Standard`;
            }
          },
          {
            key: 'features',
            display: 'Features',
            accessor: ({ cell, active, row }) => {
              return (
                <>
                  {row.app.model.features.map((f) => (
                    <S.FeaturePill key={f}>{APP_FEATURES[f]}</S.FeaturePill>
                  ))}
                </>
              );
            }
          },
          { key: 'version_control', display: 'Git provider', style: TableColumnStyle.SECONDARY },
          { key: 'git_mode', display: 'Version control', style: TableColumnStyle.SECONDARY },
          {
            key: 'row_action',
            display: '',
            shrink: true,
            accessor: (event) => {
              return (
                <AppRowWidget
                  event={event}
                  params={{
                    ...params,
                    app_id: event.row.app.id
                  }}
                />
              );
            }
          }
        ]}
        rows={normalApps}
      />
      {legacyApps.length > 0 ? (
        <>
          <S.Heading title="Apps that need to be migrated" />
          <SearchableTableWidget<AppRow>
            loading={
              !orgStore.org(params.org_id!) || (orgStore.org(params.org_id!)?.apps.loading && legacyApps.length === 0)
            }
            columns={[
              {
                key: 'name',
                display: 'App name',
                accessorSearch: (name) => {
                  return name;
                }
              },
              {
                key: 'row_action',
                display: '',
                shrink: true,
                accessor: (event) => {
                  return (
                    <AppRowWidget
                      event={event}
                      params={{
                        ...params,
                        app_id: event.row.app.id
                      }}
                    />
                  );
                }
              }
            ]}
            rows={legacyApps}
          />
        </>
      ) : null}
      <RoutesSwitch>
        <Route
          path={relativeRoute(RoutesInternal.APP_SOURCE_PROVIDER)}
          element={
            <LayerWidget<Routes.APP_SOURCE_PROVIDER>
              route={Routes.APP_SOURCE_PROVIDER}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <ChangeSourceControlForm handler={handler} />;
              }}
            />
          }
        />
        <Route
          path={relativeRoute(RoutesInternal.APP_SOURCE_MODE)}
          element={
            <LayerWidget<Routes.APP_SOURCE_MODE>
              route={Routes.APP_SOURCE_MODE}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <ChangeSourceModeForm handler={handler} />;
              }}
            />
          }
        />
        <Route
          path={relativeRoute(RoutesInternal.CREATE_APP)}
          element={
            <LayerWidget<Routes.CREATE_APP>
              route={Routes.CREATE_APP}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <SmartCreateAppPage handler={handler} />;
              }}
            />
          }
        />
        <Route
          path={relativeRoute(RoutesInternal.DELETE_APP)}
          element={
            <LayerWidget<Routes.DELETE_APP>
              route={Routes.DELETE_APP}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <DeleteAppPage handler={handler} />;
              }}
            />
          }
        />
        <Route
          path={relativeRoute(RoutesInternal.MIGRATE_APP)}
          element={
            <LayerWidget<Routes.MIGRATE_APP>
              route={Routes.MIGRATE_APP}
              options={{ withBackdrop: true, userCanExit: false }}
              renderLayer={({ handler }) => {
                return <MigrateAppPage handler={handler} />;
              }}
            />
          }
        />
      </RoutesSwitch>
    </OrgPageWidget>
  );
});

namespace S {
  export const Heading = styled(PageHeadingWidget)`
    margin-top: 20px;
  `;

  export const AppName = styled.div<{ locked: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    opacity: ${(p) => (p.locked ? 0.4 : 1)};
  `;

  export const AppOpenIcon = styled(FontAwesomeIcon)<{ hover: boolean }>`
    visibility: ${(p) => (p.hover ? 'visible' : 'hidden')};
    font-size: 25px;
    opacity: 0.5;
  `;

  export const IconContainer = styled.div<{ product: Product }>`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${(p) => getThemeForProduct(p.product).button.primary.logoBackground};
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const Icon = styled.div<{ background: string }>`
    border-radius: 10px;
    width: 30px;
    height: 30px;
    background-image: url('${(p) => p.background}');
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
  `;

  export const Rocket = styled(FontAwesomeIcon)`
    font-size: 20px;
  `;

  export const TrialExpire = styled.span`
    font-size: 12px;
    opacity: 0.5;
  `;

  export const FeaturePill = styled.div`
    padding: 5px 10px;
    background: rgba(53, 53, 53, 0.7);
    color: ${(p) => p.theme.text.secondary};
    border-radius: 15px;
    font-size: 12px;
    display: inline-block;
    white-space: nowrap;
  `;
}
